import { useReducer, useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '../../../../constants';
import { 
  initializeTimelineMatrix, 
  calculateConsecutivePenalty, 
  getGlobalTimelineStress,
  getAvailableRacesForTurn,
  autoOptimizeRoute
} from './RaceEngineUtils';

const RACE_ENGINE_ACTIONS = {
  SCHEDULE_RACE: 'SCHEDULE_RACE',
  REMOVE_RACE: 'REMOVE_RACE',
  RESET_TIMELINE: 'RESET_TIMELINE',
  AUTO_SCHEDULE_ROUTE: 'AUTO_SCHEDULE_ROUTE',
  SET_DB_RACES: 'SET_DB_RACES',
  SET_DB_ERROR: 'SET_DB_ERROR',
  SET_DB_RACES_LOADED: 'SET_DB_RACES_LOADED',
  SET_FILTER_PREFERENCES: 'SET_FILTER_PREFERENCES'
};

const initialState = {
  timeline: initializeTimelineMatrix(),
  globalStress: getGlobalTimelineStress(initializeTimelineMatrix()),
  allRacesDB: [],
  isLoadingDB: true,
  dbError: null,
  filterPreferences: { summerVacationEnabled: false, maxTriples: 0, onlyG1G2: false }
};

function raceEngineReducer(state, action) {
  switch (action.type) {
    case RACE_ENGINE_ACTIONS.SCHEDULE_RACE: {
      const { turnId, race } = action.payload;
      const targetIndex = state.timeline.findIndex(t => t.id === turnId);
      
      // Bloquear agendamiento en turnos no jugables
      if (targetIndex === -1 || !state.timeline[targetIndex].isPlayable) {
        return state;
      }

      const newTimeline = [...state.timeline];
      newTimeline[targetIndex] = { ...newTimeline[targetIndex], scheduledRace: race };

      return {
        ...state,
        timeline: newTimeline,
        globalStress: getGlobalTimelineStress(newTimeline)
      };
    }
    
    case RACE_ENGINE_ACTIONS.REMOVE_RACE: {
      const { turnId } = action.payload;
      const targetIndex = state.timeline.findIndex(t => t.id === turnId);
      
      if (targetIndex === -1) return state;

      const newTimeline = [...state.timeline];
      newTimeline[targetIndex] = { ...newTimeline[targetIndex], scheduledRace: null };

      return {
        ...state,
        timeline: newTimeline,
        globalStress: getGlobalTimelineStress(newTimeline)
      };
    }
    
    case RACE_ENGINE_ACTIONS.RESET_TIMELINE: {
      const resetTimeline = initializeTimelineMatrix();
      return {
        ...state,
        timeline: resetTimeline,
        globalStress: getGlobalTimelineStress(resetTimeline)
      };
    }
    
    case RACE_ENGINE_ACTIONS.AUTO_SCHEDULE_ROUTE: {
      const { characterAptitudes, filterPreferences } = action.payload;
      if (state.allRacesDB.length === 0) return state; // Prevenir fallo antes de cargar DB

      // Generar desde una matriz virgen asegura la máxima eficiencia del algoritmo
      const freshTimeline = initializeTimelineMatrix(); 
      const optimizedTimeline = autoOptimizeRoute(freshTimeline, characterAptitudes, state.allRacesDB, filterPreferences);
      
      return {
        ...state,
        timeline: optimizedTimeline,
        globalStress: getGlobalTimelineStress(optimizedTimeline)
      };
    }

    case RACE_ENGINE_ACTIONS.SET_DB_RACES: {
      return { ...state, allRacesDB: action.payload, isLoadingDB: false };
    }
    
    case RACE_ENGINE_ACTIONS.SET_DB_ERROR: {
      return { ...state, dbError: action.payload, isLoadingDB: false };
    }
    
    case RACE_ENGINE_ACTIONS.SET_DB_RACES_LOADED: {
      return { ...state, isLoadingDB: false };
    }

    case RACE_ENGINE_ACTIONS.SET_FILTER_PREFERENCES: {
      // Merge parcial: solo sobreescribe las keys que llegan en el payload
      return { ...state, filterPreferences: { ...state.filterPreferences, ...action.payload } };
    }

    default:
      return state;
  }
}

/**
 * Hook para gestionar el estado entero del engine, inyectando dependencias
 * para manipulación directa del estado sin acoplar UI.
 */
export const useRaceEngine = () => {
  const [state, dispatch] = useReducer(raceEngineReducer, initialState);

  // Estado de selección de personaje (UI layer — fuera del reducer)
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [selectedAptitudes, setSelectedAptitudes] = useState(null);

  // MEJORA 3 — Disparo automático al cambiar de personaje
  // Cuando hay aptitudes y la DB ya cargó, re-planifica la ruta al instante
  useEffect(() => {
    if (!selectedAptitudes || state.allRacesDB.length === 0) return;
    dispatch({
      type: RACE_ENGINE_ACTIONS.AUTO_SCHEDULE_ROUTE,
      payload: { characterAptitudes: selectedAptitudes, filterPreferences: state.filterPreferences }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAptitudes, state.allRacesDB, state.filterPreferences]);

  useEffect(() => {
    const fetchRaces = async () => {
      try {
        console.log('Racing fetch URL:', `${API_BASE_URL}/api/races`);
        const response = await fetch(`${API_BASE_URL}/api/races`);
        if (!response.ok) {
          throw new Error('Failed to fetch races');
        }
        const data = await response.json();
        dispatch({ type: RACE_ENGINE_ACTIONS.SET_DB_RACES, payload: data });
      } catch (err) {
        dispatch({ type: RACE_ENGINE_ACTIONS.SET_DB_ERROR, payload: err.message });
      } finally {
        dispatch({ type: RACE_ENGINE_ACTIONS.SET_DB_RACES_LOADED });
      }
    };
    fetchRaces();
  }, []);

  const scheduleRace = useCallback((turnId, race) => {
    dispatch({ type: RACE_ENGINE_ACTIONS.SCHEDULE_RACE, payload: { turnId, race } });
  }, []);

  const removeRace = useCallback((turnId) => {
    dispatch({ type: RACE_ENGINE_ACTIONS.REMOVE_RACE, payload: { turnId } });
  }, []);

  const resetTimeline = useCallback(() => {
    dispatch({ type: RACE_ENGINE_ACTIONS.RESET_TIMELINE });
  }, []);

  const autoSchedule = useCallback((characterAptitudes, filterPreferences = {}) => {
    dispatch({ 
      type: RACE_ENGINE_ACTIONS.AUTO_SCHEDULE_ROUTE, 
      payload: { characterAptitudes, filterPreferences } 
    });
  }, []);

  const setFilterPreferences = useCallback((partialPrefs) => {
    dispatch({ type: RACE_ENGINE_ACTIONS.SET_FILTER_PREFERENCES, payload: partialPrefs });
  }, []);

  const getAvailableRaces = useCallback((turnId) => {
    const turn = state.timeline.find(t => t.id === turnId);
    if (!turn) return [];
    return getAvailableRacesForTurn(turn, state.allRacesDB);
  }, [state.timeline, state.allRacesDB]);

  const checkPenaltyForTurn = useCallback((turnId) => {
    return calculateConsecutivePenalty(state.timeline, turnId);
  }, [state.timeline]);

  return {
    timeline: state.timeline,
    globalStress: state.globalStress,
    allRacesDB: state.allRacesDB,
    isLoadingDB: state.isLoadingDB,
    dbError: state.dbError,
    filterPreferences: state.filterPreferences,
    scheduleRace,
    removeRace,
    resetTimeline,
    autoSchedule,
    setFilterPreferences,
    getAvailableRaces,
    checkPenaltyForTurn,
    // Character selection state
    selectedCharacter,
    setSelectedCharacter,
    selectedAptitudes,
    setSelectedAptitudes
  };
};
